// Port of ios/Sources/Models/DeviceIndicator.swift + ios/Sources/ViewModels/DeviceIndicatorPresentation.swift
// (Android twin: viewmodels/DeviceIndicatorPresentation.kt).
//
// The `satoritech-device-indicator` plugin's answer (shared/PLUGINS.md §8.1), shaped for the two
// surfaces that draw it: the glyph beside the DM partner in the conversation header (which device
// NOW, or last) and the per-device drill-in ("on click, the last connection active from each
// device" — user order 2026-08-31). Owner ruling 2026-10-16: the plugin is the SOURCE OF TRUTH for
// device and last time, always shown — no server switch hides it.
//
// Pure: no DOM, no network.

/**
 * `GET /plugins/satoritech-device-indicator/device/{userId}`. `device` is the CURRENT device when
 * one is active and the LAST-SEEN one when none is — "offline" only when the plugin has never seen
 * this person. The `*Active` flags are the live half; the `*LastSeenAt` epochs (ms) the history.
 */
export interface DeviceIndicator {
  device: string;
  desktopActive: boolean;
  mobileActive: boolean;
  desktopLastSeenAt: number;
  mobileLastSeenAt: number;
  lastSeenAt: number;
  confidence: string;
  /** `desktop` / `mobile` — the plugin's own answer to "which device was last"; '' when absent. */
  lastDevice: string;
}

export type DeviceGlyph = 'desktop' | 'mobile' | 'both' | 'none';
export type DeviceKind = 'desktop' | 'mobile';

export interface DeviceRow {
  kind: DeviceKind;
  isActive: boolean;
  lastSeenAt: number;
}

/** Matches the plugin's own webapp poll order of magnitude, not its 15 s: one glyph, not a sidebar of them. */
const pollIntervalMs = 30_000;

function str(v: unknown, fallback = ''): string {
  return typeof v === 'string' ? v : fallback;
}

function num(v: unknown): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : 0;
}

/** Decoded defensively, like the Swift `init(from:)`: every missing field has its neutral value. */
function decode(json: unknown): DeviceIndicator | null {
  if (typeof json !== 'object' || json === null || Array.isArray(json)) return null;
  const o = json as Record<string, unknown>;
  return {
    device: str(o.device, 'offline') || 'offline',
    desktopActive: o.desktop_active === true,
    mobileActive: o.mobile_active === true,
    desktopLastSeenAt: num(o.desktop_last_seen_at),
    mobileLastSeenAt: num(o.mobile_last_seen_at),
    lastSeenAt: num(o.last_seen_at),
    confidence: str(o.confidence),
    lastDevice: str(o.last_device),
  };
}

/** `none` only when the plugin has never seen this person — an absent glyph is honest there. */
function glyph(indicator: DeviceIndicator | null | undefined): DeviceGlyph {
  switch (indicator?.device) {
    case 'both':
      return 'both';
    case 'desktop':
      return 'desktop';
    case 'mobile':
      return 'mobile';
    default:
      return 'none';
  }
}

/** Whether anything is connected RIGHT NOW — what colours the glyph (tinted = live, muted = last device). */
function isAnyActive(indicator: DeviceIndicator | null | undefined): boolean {
  if (!indicator) return false;
  return indicator.desktopActive || indicator.mobileActive;
}

/** Fixed order — desktop, then mobile — so each device is always in the same place. */
function rows(indicator: DeviceIndicator): DeviceRow[] {
  return [
    { kind: 'desktop', isActive: indicator.desktopActive, lastSeenAt: indicator.desktopLastSeenAt },
    { kind: 'mobile', isActive: indicator.mobileActive, lastSeenAt: indicator.mobileLastSeenAt },
  ];
}

export const DeviceIndicatorPresentation = {
  pollIntervalMs,
  decode,
  glyph,
  isAnyActive,
  rows,
} as const;
